import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Loader2, PhoneCall, CheckCircle2, Trash2, RefreshCw, User, MessageCircle,
  AlertTriangle, Wrench, Zap, HelpCircle, Search, X,
} from "lucide-react";

// Lookup table: maps raw reason → human-friendly label + icon + colour.
const REASON = {
  help_command:      { label: "HELP command",    icon: HelpCircle,    bg: "bg-[#eaf8de]", fg: "text-[#3f7c21]" },
  llm_offtopic:      { label: "Off-topic query", icon: MessageCircle, bg: "bg-[#fff0f5]", fg: "text-[#c01974]" },
  llm_error:         { label: "LLM error",       icon: Zap,           bg: "bg-[#fff6e6]", fg: "text-[#b05f00]" },
  llm_unavailable:   { label: "LLM offline",     icon: AlertTriangle, bg: "bg-[#fee2e2]", fg: "text-[#b91c1c]" },
  payout_name_mismatch: { label: "Payout name mismatch", icon: Wrench, bg: "bg-[#fff6e6]", fg: "text-[#b05f00]" },
};

const STATUS_COLOR = {
  open:     "bg-[#fff0f5] text-[#c01974]",
  ack:      "bg-[#fff6e6] text-[#b05f00]",
  resolved: "bg-[#eaf8de] text-[#3f7c21]",
};

export default function CsHandoffs() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ open: 0, ack: 0, resolved: 0, total: 0, by_reason: {} });
  const [filter, setFilter] = useState("open");
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append("status", filter);
      if (reason) params.append("reason", reason);
      const { data } = await api.get(`/cs/handoffs?${params.toString()}`);
      setRows(data.handoffs);
      setTotals(data.totals);
    } finally { setLoading(false); }
  }, [filter, reason]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, next) => {
    try {
      await api.put(`/cs/handoffs/${id}/status`, { status: next });
      load();
    } catch (e) { window.alert(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this handoff record?")) return;
    await api.delete(`/cs/handoffs/${id}`);
    load();
  };

  const q = query.trim().toLowerCase();
  const filtered = !q ? rows : rows.filter((r) => {
    return (r.name || "").toLowerCase().includes(q) ||
           (r.phone || "").toLowerCase().includes(q) ||
           (r.user_text || "").toLowerCase().includes(q);
  });

  // tel: links: strip spaces, keep +digits
  const telHref = (p) => `tel:${(p || "").replace(/[^+\d]/g, "")}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2" data-testid="cs-handoff-title">
            <PhoneCall className="h-6 w-6 text-[#ff3399]" /> Customer Care handoffs
          </h1>
          <p className="text-sm text-[#5c665a]">
            Every time the WhatsApp bot pointed a user at customer care.
            Call them back proactively — tap the phone icon to dial from your browser.
          </p>
        </div>
        <button className="admin-btn-ghost" onClick={load} data-testid="cs-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cs-status-filter-bar">
        {[
          { k: "open", label: "Open", n: totals.open },
          { k: "ack", label: "Acknowledged", n: totals.ack },
          { k: "resolved", label: "Resolved", n: totals.resolved },
          { k: "", label: `All (${totals.total || 0})`, n: null },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              filter === f.k ? "bg-[#1a1c19] text-white" : "bg-white text-[#5c665a] border border-[#e2e8e2]"
            }`}
            data-testid={`cs-status-${f.k || "all"}`}
          >
            {f.label}{f.n !== null ? ` · ${f.n}` : ""}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cs-reason-filter-bar">
        <button
          onClick={() => setReason("")}
          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${reason === "" ? "bg-[#1a1c19] border-[#1a1c19] text-white" : "bg-white border-[#e2e8e2] text-[#5c665a]"}`}
          data-testid="cs-reason-all"
        >
          All reasons
        </button>
        {Object.entries(REASON).map(([k, r]) => {
          const active = reason === k;
          const n = totals.by_reason[k] || 0;
          const Icon = r.icon;
          return (
            <button
              key={k}
              onClick={() => setReason(active ? "" : k)}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition ${active ? `${r.bg} ${r.fg} border-transparent` : "bg-white border-[#e2e8e2] text-[#5c665a]"}`}
              data-testid={`cs-reason-${k}`}
            >
              <Icon className="h-2.5 w-2.5" /> {r.label} · {n}
            </button>
          );
        })}
      </div>

      <div className="relative max-w-sm" data-testid="cs-search-wrapper">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#5c665a]" />
        <input
          type="text"
          placeholder="Search name, phone or question…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="admin-input w-full !pl-9 !pr-8 py-2 text-sm"
          data-testid="cs-search-input"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-[#5c665a] hover:bg-[#fafcfa]"
            data-testid="cs-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" /></div>
      ) : filtered.length === 0 ? (
        <div className="admin-card text-center py-10" data-testid="cs-empty">
          <CheckCircle2 className="mx-auto h-8 w-8 text-[#66cc33]" />
          <div className="mt-2 font-semibold text-[#1a1c19]">No handoffs in this bucket</div>
          <div className="text-xs text-[#5c665a]">All customers self-served — nice.</div>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto p-0" data-testid="cs-table-wrapper">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">What they said</th>
                <th className="px-4 py-3">CS phone shown</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]">
              {filtered.map((r) => {
                const meta = REASON[r.reason] || { label: r.reason, icon: HelpCircle, bg: "bg-[#fafcfa]", fg: "text-[#5c665a]" };
                const Icon = meta.icon;
                return (
                  <tr key={r.id} data-testid={`cs-row-${r.id}`} className="hover:bg-[#fafcfa]">
                    <td className="px-4 py-2 text-xs text-[#5c665a] whitespace-nowrap">{r.at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 text-sm font-semibold">
                        <User className="h-3.5 w-3.5 text-[#5c665a]" />
                        {r.name || "Unknown"}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.phone ? (
                        <a href={telHref(r.phone)} className="inline-flex items-center gap-1 rounded-full bg-[#eaf8de] px-2 py-0.5 font-bold text-[#3f7c21] hover:bg-[#d6f0c0]" data-testid={`cs-call-${r.id}`}>
                          <PhoneCall className="h-3 w-3" /> {r.phone}
                        </a>
                      ) : <span className="text-[#5c665a]">—</span>}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.bg} ${meta.fg}`}>
                        <Icon className="h-2.5 w-2.5" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 max-w-xs truncate text-xs text-[#1a1c19]" title={r.user_text}>{r.user_text}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-[#5c665a]">{r.cs_phone}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLOR[r.status] || STATUS_COLOR.open}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {r.status !== "ack" && (
                        <button className="admin-btn-ghost" onClick={() => setStatus(r.id, "ack")} data-testid={`cs-ack-${r.id}`}>
                          Ack
                        </button>
                      )}
                      {r.status !== "resolved" && (
                        <button className="admin-btn" onClick={() => setStatus(r.id, "resolved")} data-testid={`cs-resolve-${r.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Resolved
                        </button>
                      )}
                      <button className="admin-btn-ghost text-[#c01974]" onClick={() => remove(r.id)} data-testid={`cs-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
